// ============================================================
// caseFacts.ts
// Pure derivations the case HERO and the OVERVIEW share (Stage 1 shell).
// Read-only views over the existing Case + store slices — no new data.
// ============================================================
import type { Case } from '../../types/case.types'
import type { CaseDeadline } from '../../types/deadlines.types'
import { caseDobOf } from '../../utils/caseDob'
import { deadlineLaterThanEstimate, estimatedLimitation, limitationDatePassed, noTimeLimitApplies, type LaterThanEstimate, type LimitationBasis } from '../../utils/limitationRules'
import { ymdOf } from './caseUi'
import { textDateToIso } from '../../utils/civilDate'
import { claimFactsOf } from '../../utils/caseClaim'

// THE limitation deadline row: of the deadlines whose type matches
// /limitation/i, an OPEN (not completed) one first, then the EARLIEST due date
// — so a stale/duplicate row can never mask an earlier live limitation date.
// (Was the first match in list order.) Shared by the hero, Overview, quick
// view and the Deadlines section so they always agree.
export const limitationRowOf = (deadlines: CaseDeadline[] | undefined): CaseDeadline | null => {
  const rows = (deadlines ?? []).filter((d) => /limitation/i.test(d.deadlineType.name) && !!ymdOf(d.dueDate))
  if (rows.length === 0) return null
  return rows.slice().sort((a, b) =>
    Number(a.completed) - Number(b.completed) || (ymdOf(a.dueDate) ?? '').localeCompare(ymdOf(b.dueDate) ?? ''),
  )[0]
}

// Limitation: the "Limitation date" deadline when present (limitationRowOf),
// else THE estimate (utils/limitationRules estimatedLimitation — a child at
// the accident runs from their 18th birthday; no DOB → accident + 3y, flagged)
// marked estimated, with its basis.
// notes = the estimate's internal guidance (B2 — CICA child, holiday, death
// after expiry, a later date of knowledge), shown in the limitation box.
export type LimitationFact = { ymd: string; estimated: false } | { ymd: string; estimated: true; basis: LimitationBasis; notes: string[] }
export const limitationOf = (
  c: Case,
  deadlines: CaseDeadline[] | undefined,
): LimitationFact | null => {
  const lim = limitationRowOf(deadlines)
  const limYmd = lim ? ymdOf(lim.dueDate) : null
  if (limYmd) return { ymd: limYmd, estimated: false }
  // ★ (Audit) The accident date read by the SHARED rule (textDateToIso: ISO,
  // an ISO timestamp, or a UK D/M/YYYY the server accepts) — ymdOf is ISO-only,
  // so a "15/03/2022" accident gave no estimate here while the quick view, the
  // email and the briefing showed one.
  const est = estimatedLimitation(textDateToIso(c.accidentDetails.accidentDate), caseDobOf(c.clientDetails?.dateOfBirth, c.client?.dateOfBirth).limitationDob, claimFactsOf(c.claim, c.protectedParty))
  return est ? { ymd: est.ymd, estimated: true, basis: est.basis, notes: est.notes } : null
}

// ★ (Audit M6) The estimate's CHECKS (capacity, death after expiry, a later
// date of knowledge, CICA / holiday, fatal) — worked out from the case's facts
// whatever its Limitation date is, so they still show under a saved
// Limitation deadline (and under one made from the own date: the own date is
// left out here, so the rules' notes come through).
export const limitationChecksOf = (c: Case): string[] =>
  estimatedLimitation(
    textDateToIso(c.accidentDetails.accidentDate),
    caseDobOf(c.clientDetails?.dateOfBirth, c.client?.dateOfBirth).limitationDob,
    { ...claimFactsOf(c.claim, c.protectedParty), overrideDate: null },
  )?.notes ?? []

// (B2b) Child sexual abuse confirmed as having NO time limit (s.11ZA) — then
// there's no estimate (limitationOf → null unless a deadline is saved) and the
// case is never "No limitation date". null = not confirmed / doesn't apply
// (or an own date is set — the own date wins).
export const noTimeLimitOf = (c: Case): { confirmedAt: string } | null => {
  const facts = claimFactsOf(c.claim, c.protectedParty)
  if (facts.overrideDate) return null
  const ok = noTimeLimitApplies(textDateToIso(c.accidentDetails.accidentDate), caseDobOf(c.clientDetails?.dateOfBirth, c.client?.dateOfBirth).limitationDob, facts)
  return ok && c.claim?.abuseNoLimitConfirmedAt ? { confirmedAt: c.claim.abuseNoLimitConfirmedAt } : null
}

// (B2b fix) The case's OPEN Limitation deadline (the pinned row) is LATER
// than Casevo's estimate (its facts, ignoring the deadline and the own date)?
// → the amber check at the top of Checks and the hero / quick-view dot.
// null while the deadlines are loading, with no open limitation row, or when
// the deadline is on / before the estimate (or no time limit applies).
export const limitationLaterCheckOf = (c: Case, deadlines: CaseDeadline[] | undefined): LaterThanEstimate | null => {
  const row = limitationRowOf(deadlines)
  if (!row || row.completed) return null
  return deadlineLaterThanEstimate(
    ymdOf(row.dueDate),
    textDateToIso(c.accidentDetails.accidentDate),
    caseDobOf(c.clientDetails?.dateOfBirth, c.client?.dateOfBirth).limitationDob,
    claimFactsOf(c.claim, c.protectedParty),
  )
}

// (B2b fix 2) The case's limitation date has PASSED: the open (pinned)
// Limitation deadline, or — with none saved — the estimate / own date, is
// before today. A completed deadline (done) isn't flagged; nothing while the
// deadlines are loading.
export const limitationPassedOf = (c: Case, deadlines: CaseDeadline[] | undefined, today: string): boolean => {
  if (deadlines === undefined) return false
  const row = limitationRowOf(deadlines)
  if (row?.completed) return false
  return limitationDatePassed(limitationOf(c, deadlines)?.ymd, today)
}

// Open (not completed) deadlines, soonest first.
export const openDeadlinesOf = (deadlines: CaseDeadline[] | undefined): CaseDeadline[] =>
  (deadlines ?? [])
    .filter((d) => !d.completed && !!ymdOf(d.dueDate))
    .sort((a, b) => (ymdOf(a.dueDate) ?? '').localeCompare(ymdOf(b.dueDate) ?? ''))

// Injury areas ticked (labels as InjuryDetailsSection.tsx:19-31).
const INJURY_LABELS: [keyof Case['injuryDetails'], string][] = [
  ['neck', 'Neck'], ['shoulder', 'Shoulder'], ['back', 'Back'], ['ribsChestTorso', 'Ribs/chest/torso'],
  ['faceCheckboneJawNose', 'Face/jaw/nose'], ['forearmWristHandFingers', 'Arm/wrist/hand'],
  ['damageToTeeth', 'Teeth'], ['headSenses', 'Head/senses'], ['hipsPelvisGenitals', 'Hips/pelvis'],
  ['legKneeAnkleFootToe', 'Leg/knee/foot'], ['psychological', 'Psychological'],
  ['hearingEars', 'Hearing/ears'], ['lungsBreathing', 'Lungs/breathing'], ['skin', 'Skin'], ['internalOrgans', 'Internal organs'],
  ['fatal', 'Fatal'], ['other', 'Other'],
]
export const injuryAreasOf = (c: Case): string[] =>
  INJURY_LABELS.filter(([k]) => c.injuryDetails[k] === true).map(([, l]) => l)

// "Neck · Back +2"
export const injurySummary = (areas: string[]): string =>
  areas.length === 0 ? '' : areas.length <= 2 ? areas.join(' · ') : `${areas.slice(0, 2).join(' · ')} +${areas.length - 2}`

// Accident description label ('hit in the rear' → 'Hit in the rear'; 'other' → the free text).
export const accidentDescriptionOf = (c: Case): string => {
  const a = c.accidentDetails
  if (!a.accidentDescription) return ''
  if (a.accidentDescription === 'other') return a.accidentDescriptionOther.trim() || 'Other'
  return a.accidentDescription.charAt(0).toUpperCase() + a.accidentDescription.slice(1)
}

// Short place: town · postcode (address mode), or the free text (tags stripped).
export const accidentPlaceOf = (c: Case): string => {
  const a = c.accidentDetails
  if (a.accidentLocationMode === 'freetext') {
    return a.accidentLocationFreeText.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
  }
  const parts = [a.accidentLocationAddressTown.trim(), a.accidentLocationAddressPostcode.trim()].filter(Boolean)
  return parts.length ? parts.join(' ') : a.accidentLocation.trim()
}
